import React from 'react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';

import { formatAmountValue, pluralize, sqlDateToLabel, fractionToPercent } from 'helpers/helpers';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useStyles from '../Targets/TargetsStyle';
import { Deposit, DepositFrequency } from 'stores/workflowStores/finances/depositsStore/model';
import DepositSingleIcon from 'components/Icons/DepositSingleIcon';
import DepositRegularIcon from 'components/Icons/DepositRegularIcon';
import DepositEmployerIcon from 'components/Icons/DepositEmployerIcon';
import { Currency, EdwardColors } from 'enums';

interface Props {
  currency: Currency;
  deposit: Deposit;
  annual?: boolean;
  actualInflation: number;
  type?: string;
  original?: boolean;
}

const GeneralDeposit: React.FC<Props> = ({ deposit, currency, annual, actualInflation, type, original }) => {
  const { t } = useTranslation();
  const styles = useGeneralStyles();
  const classes = useStyles();

  return (
    <Grid container item xs={12} spacing={1}>
      <Grid container item alignContent="center" alignItems="center" className={classes.target}>
        <Grid item xs={1} className={classes.icon}>
          {type === 'employer' ? (
            <DepositEmployerIcon fontSize="inherit" htmlColor={original ? EdwardColors.GRAY_MEDIUM : undefined} />
          ) : deposit.frequency === DepositFrequency.SINGLE ? (
            <DepositSingleIcon fontSize="inherit" htmlColor={original ? EdwardColors.GRAY_MEDIUM : undefined} />
          ) : (
            <DepositRegularIcon fontSize="inherit" htmlColor={original ? EdwardColors.GRAY_MEDIUM : undefined} />
          )}
        </Grid>
        <Grid item xs={7}>
          <span className={styles.mediumText}>{deposit.title || t(`deposits_${deposit.type || type}`)}</span>
          <br />
          {deposit.frequency === DepositFrequency.SINGLE ? (
            sqlDateToLabel(deposit.since!)
          ) : (
            <>
              {t('general_since')}: {sqlDateToLabel(deposit.since!)} {t('general_until')}:{' '}
              {sqlDateToLabel(deposit.until!)}
            </>
          )}
          {deposit.inflation?.enabled && <> | plánovaná inflace: {fractionToPercent(deposit.inflation.rate)}</>}
          {deposit.active && (
            <>
              {' | '}
              <strong>{t('additionalTargets_active')}</strong>
            </>
          )}
        </Grid>
        <Grid item xs={2}>
          <span className={styles.mediumText}>
            {annual && deposit.inflation!.valuation ? (
              <></>
            ) : (
              <>{formatAmountValue(deposit.value!.value, currency).replace(/ /g, '\u00A0')}</>
            )}
          </span>
          <br />
          {deposit.frequency === DepositFrequency.REPEAT_EVERY ? (
            <>
              {' '}
              {t('additionalTargets_every')} {deposit.repeatYears}{' '}
              {pluralize(deposit.repeatYears!, t('general_repeatYear'), t('general_repeatYears'))}
            </>
          ) : (
            t(`general_frequency_${deposit.frequency}`)
          )}{' '}
        </Grid>
        {annual && deposit.inflation!.valuation && (
          <>
            <Grid item xs={12} className={`${styles.bold} ${styles.regularText}`}>
              Částka vkladu se automaticky navyšuje podle{' '}
              {deposit.inflation!.type === 'ACTUAL' ? 'skutečné' : 'nastavené'} inflace{' '}
              {fractionToPercent(deposit.inflation!.type === 'PLANNED' ? deposit.inflation!.rate : actualInflation)} na{' '}
              <span className={styles.mediumText}>{formatAmountValue(deposit.value!.value, currency)}</span>
            </Grid>
          </>
        )}
      </Grid>
    </Grid>
  );
};

export default GeneralDeposit;
